import Link from 'next/link';
import { TScheduleShort } from '@/domain/Availability.domain';
import { getDateDisplay } from '@/service/utils/getDateDisplay';
import { TAvailItem } from '@/service/state/FullCalendar/domain';
import { Schedules } from '@/ui/common/Schedules';
import { useAvailabilityStore } from '@/service/state/Availability/useAvailabilityStore';
import { newDateFromString } from '@/service/utils/getDateByTimezone';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
import { FormButton } from '@/ui/common/Buttons/FormButton';

type TTripTimesDialogProps = {
    trip: TAvailItem;
    handleClick?: () => void;
    handleGoToBookingPage?: () => void;
};

export const TripTimesDialogCaption = ({ trip }: { trip: TScheduleShort | undefined }) => (
    <div className="mx-auto w-full max-w-full px-[18px] md:max-w-[80%] md:px-9 lg:pl-[70px] lg:pr-20">
        {!!trip?.date && (
            <p className="tx-large-medium mb-2 text-center text-gray-2 dark:text-gray-3">
                {getDateDisplay(newDateFromString(trip.date))}
            </p>
        )}
        <p className="tx-large-semibold text-gray-1 dark:text-white">{trip?.name}</p>
    </div>
);

export const TripTimesDialog = ({ trip, handleGoToBookingPage }: TTripTimesDialogProps) => {
    const { selectedDate, getSelectedDay, selectSchedule, loadingDay } = useAvailabilityStore();
    const selectedDay = getSelectedDay(selectedDate);
    const isLoadingDay = !selectedDate || !selectedDay || loadingDay;

    return (
        <div
            data-test-id="calendar-schedules-list"
            className="relative flex h-full flex-col justify-between pb-2 md:w-[400px] md:px-6 md:py-4"
        >
            <p className="tx-large-medium px-4 text-gray-1 dark:text-ash md:p-0">{trip.name}</p>
            {!!selectedDate && (
                <p
                    data-test-id="tickets-date"
                    className="after-shadow tx-large-semibold relative mt-3 flex items-center justify-between px-4 text-gray-2 after:-bottom-4 after:rotate-180 dark:text-gray-3 md:p-0 after:md:hidden"
                >
                    {!!trip.price && (
                        <div className="tx-medium-medium flex items-center rounded-2xl bg-blue-10 px-4 py-1 dark:bg-brand-dark">
                            from&nbsp;
                            <span className="tx-heading-4">{getFormattedPrice(+trip.price)}</span>
                        </div>
                    )}
                    {getDateDisplay(newDateFromString(selectedDate), 'EEEE, MMMM d') + 'th'}
                </p>
            )}
            <div className="custom-scroll h-full max-h-full overflow-auto">
                <Schedules
                    list={selectedDay?.[0]?.schedules}
                    isLoading={isLoadingDay}
                    selectSchedule={(scheduleId) => {
                        selectSchedule(scheduleId);
                    }}
                    isUseSchedule={trip.isUseSchedule}
                    isUseCalendar={trip.isUseCalendar}
                    hasOvernights={selectedDay?.[0]?.has_overnights}
                />
            </div>
            <div className="after-shadow relative px-4 after:-top-3.5 md:p-0 after:md:hidden">
                <p className="tx-large-medium flex justify-center text-gray-3 dark:text-dark-1">
                    Or
                </p>
                <Link
                    href={trip.slug}
                    passHref
                    className="w-full"
                    onClick={handleGoToBookingPage}
                    data-test-id="calendar-schedules-trip-link"
                    prefetch={false}
                >
                    <FormButton
                        className="h-12 w-full max-w-full"
                        label="Go to activity’s page"
                        disabled={false}
                        onClick={() => {
                            return;
                        }}
                        data-test-id="go-to-activity"
                    />
                </Link>
            </div>
        </div>
    );
};
